// UI-2: Chip and ChipGroup. design-brief.md "Chips"; addendum 4 (a phone scrolls a chip
// row sideways; four chips or fewer may wrap instead).
//
// Single select is a radiogroup (one choice; arrows move it, as a radio group does). Multi
// select is a group of toggle buttons (aria-pressed), each in the Tab order.
import React, { useRef } from 'react';
import { v } from './tokens';
import { PriorityDot } from './Priority';
import './v2.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// color / dot (decision 50, a priority chip): a selected chip fills with its own colour instead
// of the highlight green, keeping the charcoal outline; dot adds High's small red dot.
const colored = (color) => (color ? { style: { '--ui-bg': v(color) } } : {});

// A lone chip (a filter or a "Sort" control): a toggle button.
export function Chip({ selected = false, onClick, children, className, color, dot = false, ...rest }) {
  return (
    <button type="button" aria-pressed={!!selected} className={cx('ui-chip', 'ui-hit', color && 'ui-chip--colored', className)} onClick={onClick} {...colored(color)} {...rest}>
      {dot && <PriorityDot />}{children}
    </button>
  );
}

// options: [{ value, label, icon?, color?, dot? }]. single: value is one value; multi: an array.
export function ChipGroup({ options, value, onChange, multi = false, label, scroll }) {
  const ref = useRef(null);
  // Five or more chips scroll sideways on a phone; four or fewer wrap (addendum 4).
  const scrolls = scroll ?? options.length > 4;
  const isOn = (v) => (multi ? (value || []).includes(v) : value === v);

  const toggle = (v) => {
    if (!multi) return onChange(v);
    const set = new Set(value || []);
    if (set.has(v)) set.delete(v); else set.add(v);
    return onChange(options.map((o) => o.value).filter((x) => set.has(x)));
  };

  const onKey = (e) => {
    if (multi) return;
    const i = options.findIndex((o) => o.value === value);
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % options.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + options.length) % options.length;
    if (next === null) return;
    e.preventDefault();
    onChange(options[next].value);
    const buttons = ref.current?.querySelectorAll('[role="radio"]');
    setTimeout(() => buttons?.[next]?.focus(), 0);
  };

  return (
    <div ref={ref} role={multi ? 'group' : 'radiogroup'} aria-label={label}
      className={cx('ui-chips', scrolls && 'ui-chips--scroll')} onKeyDown={onKey}>
      {options.map((o) => {
        const on = isOn(o.value);
        return multi ? (
          <button key={o.value} type="button" aria-pressed={on} data-tour-id={o.tourId} className={cx('ui-chip', 'ui-hit', o.color && 'ui-chip--colored')} {...colored(o.color)} onClick={() => toggle(o.value)}>
            {o.dot && <PriorityDot />}{o.icon && <span aria-hidden="true">{o.icon}</span>}{o.label}
          </button>
        ) : (
          <button key={o.value} type="button" role="radio" aria-checked={on} data-tour-id={o.tourId /* a guided tour can point at one choice */}
            tabIndex={on || (value === undefined && o === options[0]) ? 0 : -1}
            className={cx('ui-chip', 'ui-hit', o.color && 'ui-chip--colored')} {...colored(o.color)} onClick={() => toggle(o.value)}>
            {o.dot && <PriorityDot />}{o.icon && <span aria-hidden="true">{o.icon}</span>}{o.label}
          </button>
        );
      })}
    </div>
  );
}
